import {
  DeliveryArea,
  OrderOption,
  OrderOptionForm,
  OrderType,
  OrderValueLimit,
  OrderValueLimitForm,
  PaymentConfig,
} from 'api/ordering-service/ordering-service.types';
import omit from 'lodash.omit';
import { DateTime } from 'luxon';

const today = DateTime.now().toFormat('yyyy-MM-dd');

export const transformClosedTodayForRequest = (
  isClosedToday,
  existingOffDays,
) => {
  if (isClosedToday) {
    return [...existingOffDays, today];
  }

  return existingOffDays?.filter(offDay => offDay !== today);
};

export const transformClosedTodayForResponse = offDays => {
  return offDays?.includes(today);
};

export const transformPaymentConfigsForRequest = (
  pickupPaymentConfigs?: PaymentConfig,
  deliveryPaymentConfigs?: PaymentConfig,
  pickupOrdersAccepted?: boolean,
  deliveryOrdersAccepted?: boolean,
  existingPaymentConfigs?: PaymentConfig[],
) => {
  const formatOrder = input =>
    input?.paymentModes
      ? { ...input, paymentModes: [input.paymentModes] }
      : null;

  // When an order type is switched off, keep its saved config instead of
  // dropping it, but don't create a default config for it either
  const existingConfig = (orderType: OrderType) =>
    existingPaymentConfigs?.find(config => config.orderType === orderType);

  return [
    pickupOrdersAccepted
      ? formatOrder(pickupPaymentConfigs)
      : existingConfig('PICKUP'),
    deliveryOrdersAccepted
      ? formatOrder(deliveryPaymentConfigs)
      : existingConfig('DELIVERY'),
  ].filter(Boolean);
};

export const transformPaymentConfigsForResponse = (
  paymentConfigs: PaymentConfig[],
  orderType: OrderType,
) => {
  const config = paymentConfigs?.find(e => e.orderType === orderType);

  if (config) {
    return { ...config, paymentModes: config.paymentModes.toString() };
  }
  return undefined;
};

export const transformOrderValueLimitsForResponse = (
  orderValueLimits: OrderValueLimit,
): OrderValueLimitForm =>
  orderValueLimits
    ? {
        orderValueLimitsEnabled: true,
        minOrderValue:
          orderValueLimits.minOrderValue == null
            ? null
            : orderValueLimits.minOrderValue / 1000,
        maxOrderValue:
          orderValueLimits.maxOrderValue == null
            ? null
            : orderValueLimits.maxOrderValue / 1000,
        displayMessage: orderValueLimits.displayMessage.map(message => ({
          languageCode: message.langCode,
          shortMessage: message.shortMessage,
          longMessage: message.longMessage,
        })),
      }
    : {
        orderValueLimitsEnabled: false,
        minOrderValue: null,
        maxOrderValue: null,
        displayMessage: [
          {
            languageCode: 'EN',
            shortMessage: '',
            longMessage: '',
          },
        ],
      };

export const transformDeliveryAreaForResponse = (
  deliveryArea: DeliveryArea,
): DeliveryArea => {
  return {
    ...deliveryArea,
    deliveryAreaPolygon: deliveryArea.deliveryAreaPolygon ?? [],
  };
};

export const transformOrderValueLimitsForRequest = (
  orderValueLimits: OrderValueLimitForm,
): OrderValueLimit =>
  orderValueLimits?.orderValueLimitsEnabled
    ? {
        minOrderValue:
          orderValueLimits.minOrderValue == null
            ? null
            : orderValueLimits.minOrderValue * 1000,
        maxOrderValue:
          orderValueLimits.maxOrderValue == null
            ? null
            : orderValueLimits.maxOrderValue * 1000,
        displayMessage: orderValueLimits.displayMessage.map(message => ({
          langCode: message.languageCode,
          shortMessage: message.shortMessage,
          longMessage: message.longMessage,
        })),
      }
    : null;

export const transformOrderOptionsForResponse = (
  orderOptions: OrderOption[],
): OrderOptionForm[] => {
  if (!orderOptions) {
    return [];
  }

  return [...orderOptions]
    .sort((a, b) => a.displayRank - b.displayRank)
    .map((option, i) => ({
      optionName: option.optionName,
      displayRank: i,

      enableOrderCompleteEmail:
        option.channelsForOrderCompleteNotification?.includes('EMAIL'),
      enableOrderCompleteSMS:
        option.channelsForOrderCompleteNotification?.includes('SMS'),
      enableOrderReceivedEmail:
        option.channelsForOrderReceivedNotification?.includes('EMAIL'),
      enableOrderReceivedSMS:
        option.channelsForOrderReceivedNotification?.includes('SMS'),
      enableImHere: option.enableImHere,
      enableInformationField: option.enableInformationField,
      makeInformationFieldMandatory: option.makeInformationFieldMandatory,
      enableInstructions: option.enableInstructions,

      displayMessage: option.displayMessage.map(message => ({
        ...message,
        languageCode: message.langCode,
      })),
    }));
};

export const transformOrderOptionsForRequest = (
  orderOptions: OrderOptionForm[],
): OrderOption[] =>
  orderOptions?.map((option, i) => ({
    optionName: option.optionName,
    displayRank: i,

    channelsForOrderCompleteNotification: [
      option.enableOrderCompleteEmail ? 'EMAIL' : null,
      option.enableOrderCompleteSMS ? 'SMS' : null,
    ].filter(Boolean) as Array<'EMAIL' | 'SMS'>,
    channelsForOrderReceivedNotification: [
      option.enableOrderReceivedEmail ? 'EMAIL' : null,
      option.enableOrderReceivedSMS ? 'SMS' : null,
    ].filter(Boolean) as Array<'EMAIL' | 'SMS'>,
    enableImHere: option.enableImHere,
    enableInformationField: option.enableInformationField,
    makeInformationFieldMandatory: option.makeInformationFieldMandatory,
    enableInstructions: option.enableInstructions,

    displayMessage: option.displayMessage.map(message => ({
      ...omit(message, 'languageCode'),
      langCode: message.languageCode,
      errorMessageIfInformationFieldIsEmpty:
        option.makeInformationFieldMandatory
          ? message.errorMessageIfInformationFieldIsEmpty
          : null,
    })),
  }));

export const transformDeliveryAreaForRequest = (
  deliveryArea: DeliveryArea,
): DeliveryArea => {
  if (!deliveryArea) return null;

  return {
    deliveryAreaType: deliveryArea.deliveryAreaType,
    deliveryAreaPolygon:
      deliveryArea.deliveryAreaType === 'POLYGON'
        ? deliveryArea.deliveryAreaPolygon
        : null,
  };
};

export const transformDeliveryRadius = (
  deliveryArea: DeliveryArea,
  deliveryRadius: number,
) => {
  return deliveryArea?.deliveryAreaType === 'CIRCLE' ? deliveryRadius : null;
};
